import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DialpadIcon from "@mui/icons-material/Dialpad";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";

type GeneratorMode = "password" | "passphrase" | "pin";
type SymbolSet = "basic" | "extended" | "custom";
type WordStyle = "lower" | "title" | "upper";

interface PasswordOptions {
  length: number;
  includeLowercase: boolean;
  includeUppercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  includeSpaces: boolean;
  requireEveryType: boolean;
  excludeSimilar: boolean;
  excludeAmbiguous: boolean;
  excludeCharacters: string;
  symbolSet: SymbolSet;
  customSymbols: string;
  noRepeatedCharacters: boolean;
  avoidSequentialCharacters: boolean;
  startWithLetter: boolean;
  endWithNumber: boolean;
}

interface PassphraseOptions {
  wordCount: number;
  separator: string;
  wordStyle: WordStyle;
  uniqueWords: boolean;
  appendNumber: boolean;
  numberDigits: number;
  appendSymbol: boolean;
  symbolPool: string;
}

interface PinOptions {
  length: number;
  avoidLeadingZero: boolean;
  noRepeatedDigits: boolean;
  avoidSequentialDigits: boolean;
}

const LOWERCASE_CHARS = "abcdefghijklmnopqrstuvwxyz";
const UPPERCASE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const NUMBER_CHARS = "0123456789";
const BASIC_SYMBOLS = "!@#$%^&*-_=+?";
const EXTENDED_SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>/?|~";
const SIMILAR_CHARS = "il1Lo0O";
const AMBIGUOUS_SYMBOLS = "{}[]()/\\'\"`~,;:.<>";
const FALLBACK_SYMBOLS = "!@#$%";

const WORD_BANK = [
  "amber",
  "anchor",
  "apple",
  "april",
  "arrow",
  "ash",
  "atlas",
  "aurora",
  "autumn",
  "bamboo",
  "beacon",
  "berry",
  "blaze",
  "bloom",
  "blue",
  "bridge",
  "brook",
  "cactus",
  "candle",
  "canyon",
  "cedar",
  "cherry",
  "cloud",
  "coast",
  "comet",
  "copper",
  "coral",
  "cosmic",
  "crystal",
  "daisy",
  "dawn",
  "delta",
  "desert",
  "dream",
  "drift",
  "echo",
  "ember",
  "emerald",
  "falcon",
  "feather",
  "field",
  "firefly",
  "fjord",
  "flame",
  "flora",
  "forest",
  "frost",
  "galaxy",
  "garden",
  "glade",
  "glow",
  "granite",
  "harbor",
  "harmony",
  "hazel",
  "horizon",
  "island",
  "jade",
  "jasmine",
  "jungle",
  "juniper",
  "keystone",
  "lagoon",
  "lantern",
  "laurel",
  "lava",
  "legend",
  "lemon",
  "lilac",
  "lotus",
  "lunar",
  "maple",
  "marble",
  "meadow",
  "meteor",
  "midnight",
  "mist",
  "monsoon",
  "moon",
  "morning",
  "mountain",
  "nectar",
  "nova",
  "oasis",
  "ocean",
  "olive",
  "onyx",
  "opal",
  "orange",
  "orchid",
  "otter",
  "pearl",
  "pebble",
  "pepper",
  "phoenix",
  "pine",
  "planet",
  "plume",
  "prairie",
  "prime",
  "quartz",
  "quest",
  "quill",
  "rain",
  "raven",
  "reef",
  "ridge",
  "river",
  "rocket",
  "rose",
  "ruby",
  "sage",
  "saffron",
  "sail",
  "scarlet",
  "shadow",
  "shore",
  "silver",
  "sky",
  "solstice",
  "spark",
  "spring",
  "spruce",
  "star",
  "stone",
  "storm",
  "summer",
  "summit",
  "sun",
  "sunrise",
  "sunset",
  "swift",
  "terra",
  "thunder",
  "tidal",
  "timber",
  "trail",
  "traveler",
  "tulip",
  "valley",
  "velvet",
  "violet",
  "wave",
  "willow",
  "wind",
  "winter",
  "woodland",
  "zenith",
  "almond",
  "aster",
  "badger",
  "bay",
  "birch",
  "branch",
  "breeze",
  "brooklyn",
  "caper",
  "cardinal",
  "cliff",
  "cobalt",
  "cove",
  "creek",
  "dune",
  "elm",
  "fern",
  "finch",
  "fox",
  "glacier",
  "grove",
  "hawk",
  "heather",
  "iris",
  "ivy",
  "lake",
  "leaf",
  "linen",
  "mango",
  "marina",
  "moss",
  "north",
  "palm",
  "petal",
  "pond",
  "rainbow",
  "robin",
  "sand",
  "sea",
  "shell",
  "snow",
  "sprout",
  "stream",
  "tea",
  "topaz",
  "tree",
  "tundra",
  "vine",
  "water",
  "west",
  "wild",
  "wren",
  "yarrow",
];

const passwordFaqItems = [
  {
    question: "What kinds of passwords can I generate here?",
    answer:
      "You can create random passwords, memorable passphrases and numeric PINs. The page supports advanced controls such as custom symbols, excluded characters, no repeated characters, no sequential characters, unique passphrase words and more.",
  },
  {
    question: "Does the password generator run locally in the browser?",
    answer:
      "Yes. Password creation happens client-side in the browser so generated passwords are not sent to a server for processing.",
  },
  {
    question: "Can I generate multiple passwords at once?",
    answer:
      "Yes. You can generate a batch of results in one run, copy a single result, or copy the full list at once for account setup or password manager imports.",
  },
  {
    question: "Does the page show password strength?",
    answer:
      "Yes. The generator estimates entropy in bits and labels the result as weak, fair, strong or very strong so you can quickly tune the configuration.",
  },
] as const;

const passwordUseCases = [
  "Create strong app, banking and email passwords with custom rules",
  "Generate memorable passphrases for password managers and recovery kits",
  "Produce secure PIN codes for devices, vaults and verification flows",
  "Batch-generate credentials for demos, staging accounts and testing",
  "Exclude confusing characters for easier typing and sharing",
  "Tune length, symbols, separators and uniqueness for your security policy",
];

const uniqueCharacters = (value: string) =>
  Array.from(new Set(value.split(""))).join("");

const getCryptoRandomInt = (max: number) => {
  if (max <= 0) {
    return 0;
  }

  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const limit = Math.floor(0xffffffff / max) * max;
    const values = new Uint32Array(1);

    do {
      window.crypto.getRandomValues(values);
    } while (values[0] >= limit);

    return values[0] % max;
  }

  return Math.floor(Math.random() * max);
};

const pickRandomCharacter = (pool: string) =>
  pool[getCryptoRandomInt(pool.length)];

const shuffleCharacters = (characters: string[]) => {
  const copy = [...characters];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = getCryptoRandomInt(index + 1);
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }

  return copy;
};

const maskSensitiveValue = (value: string) => value.replace(/[^\s]/g, "•");

const formatDisplayNumber = (value: number) => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  if (value >= 100) {
    return Math.round(value).toString();
  }

  return value.toFixed(1).replace(/\.0$/, "");
};

const toTitleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const applyWordStyle = (value: string, style: WordStyle) => {
  switch (style) {
    case "title":
      return toTitleCase(value);
    case "upper":
      return value.toUpperCase();
    default:
      return value.toLowerCase();
  }
};

const hasSequentialCharacters = (value: string) => {
  for (let index = 1; index < value.length; index += 1) {
    const previous = value.charCodeAt(index - 1);
    const current = value.charCodeAt(index);

    if (Math.abs(current - previous) === 1) {
      return true;
    }
  }

  return false;
};

const getStrengthMeta = (mode: GeneratorMode, entropyBits: number) => {
  if (mode === "pin") {
    if (entropyBits < 12) {
      return { label: "Weak", color: "error" as const };
    }
    if (entropyBits < 18) {
      return { label: "Fair", color: "warning" as const };
    }
    if (entropyBits < 24) {
      return { label: "Strong", color: "success" as const };
    }

    return { label: "Very strong", color: "primary" as const };
  }

  if (mode === "passphrase") {
    if (entropyBits < 36) {
      return { label: "Weak", color: "error" as const };
    }
    if (entropyBits < 56) {
      return { label: "Fair", color: "warning" as const };
    }
    if (entropyBits < 72) {
      return { label: "Strong", color: "success" as const };
    }

    return { label: "Very strong", color: "primary" as const };
  }

  if (entropyBits < 40) {
    return { label: "Weak", color: "error" as const };
  }
  if (entropyBits < 60) {
    return { label: "Fair", color: "warning" as const };
  }
  if (entropyBits < 80) {
    return { label: "Strong", color: "success" as const };
  }

  return { label: "Very strong", color: "primary" as const };
};

const getPinEntropy = (options: PinOptions) => {
  let combinations = options.avoidLeadingZero ? 9 : 10;
  let remainingUniqueDigits = options.avoidLeadingZero ? 9 : 10;

  for (let index = 1; index < options.length; index += 1) {
    if (options.noRepeatedDigits) {
      combinations *= remainingUniqueDigits;
      remainingUniqueDigits -= 1;
    } else {
      combinations *= 10;
    }
  }

  return combinations > 0 ? Math.log2(combinations) : 0;
};

const getFilteredPool = (
  pool: string,
  excludeCharacters: string,
  excludeSimilar: boolean,
  excludeAmbiguous: boolean,
) => {
  const excludeSet = new Set(excludeCharacters.split(""));

  return uniqueCharacters(
    pool
      .split("")
      .filter((character) => {
        if (excludeSet.has(character)) {
          return false;
        }
        if (excludeSimilar && SIMILAR_CHARS.includes(character)) {
          return false;
        }
        if (excludeAmbiguous && AMBIGUOUS_SYMBOLS.includes(character)) {
          return false;
        }
        return true;
      })
      .join(""),
  );
};

const getPasswordPools = (options: PasswordOptions) => {
  const lowercase = options.includeLowercase
    ? getFilteredPool(
        LOWERCASE_CHARS,
        options.excludeCharacters,
        options.excludeSimilar,
        false,
      )
    : "";
  const uppercase = options.includeUppercase
    ? getFilteredPool(
        UPPERCASE_CHARS,
        options.excludeCharacters,
        options.excludeSimilar,
        false,
      )
    : "";
  const numbers = options.includeNumbers
    ? getFilteredPool(
        NUMBER_CHARS,
        options.excludeCharacters,
        options.excludeSimilar,
        false,
      )
    : "";

  const selectedSymbolPool =
    options.symbolSet === "custom"
      ? options.customSymbols || FALLBACK_SYMBOLS
      : options.symbolSet === "extended"
        ? EXTENDED_SYMBOLS
        : BASIC_SYMBOLS;

  const symbols = options.includeSymbols
    ? getFilteredPool(
        selectedSymbolPool,
        options.excludeCharacters,
        false,
        options.excludeAmbiguous,
      )
    : "";

  const letters = uniqueCharacters(`${lowercase}${uppercase}`);
  const all = uniqueCharacters(
    `${lowercase}${uppercase}${numbers}${symbols}${options.includeSpaces ? " " : ""}`,
  );

  return { lowercase, uppercase, numbers, symbols, letters, all };
};

const validatePasswordConfiguration = (options: PasswordOptions) => {
  const pools = getPasswordPools(options);
  const selectedTypes = [
    options.includeLowercase && pools.lowercase,
    options.includeUppercase && pools.uppercase,
    options.includeNumbers && pools.numbers,
    options.includeSymbols && pools.symbols,
  ].filter(Boolean).length;

  if (!pools.all) {
    return "Choose at least one character type to generate passwords.";
  }

  if (options.startWithLetter && !pools.letters) {
    return "Enable lowercase or uppercase letters to start with a letter.";
  }

  if (options.endWithNumber && !pools.numbers) {
    return "Enable numbers to end the password with a digit.";
  }

  const reservedSlots =
    Number(options.startWithLetter) + Number(options.endWithNumber);
  const requiredTypeSlots = options.requireEveryType ? selectedTypes : 0;

  if (options.length < reservedSlots + requiredTypeSlots) {
    return "Increase the password length or relax the required character rules.";
  }

  if (options.noRepeatedCharacters && options.length > pools.all.length) {
    return "This configuration needs more unique characters or a shorter length.";
  }

  return "";
};

const generatePassword = (options: PasswordOptions) => {
  const pools = getPasswordPools(options);
  const selectedGroups = [
    options.includeLowercase ? pools.lowercase : "",
    options.includeUppercase ? pools.uppercase : "",
    options.includeNumbers ? pools.numbers : "",
    options.includeSymbols ? pools.symbols : "",
  ].filter(Boolean);

  for (let attempt = 0; attempt < 600; attempt += 1) {
    const fixedPrefix = options.startWithLetter
      ? pickRandomCharacter(pools.letters)
      : "";
    const fixedSuffix = options.endWithNumber
      ? pickRandomCharacter(pools.numbers)
      : "";

    const middle: string[] = [];
    const used = new Set<string>();

    if (options.noRepeatedCharacters) {
      fixedPrefix && used.add(fixedPrefix);
      fixedSuffix && used.add(fixedSuffix);
    }

    if (options.requireEveryType) {
      for (const group of selectedGroups) {
        const filteredGroup = options.noRepeatedCharacters
          ? group
              .split("")
              .filter((character) => !used.has(character))
              .join("")
          : group;

        if (!filteredGroup) {
          continue;
        }

        const character = pickRandomCharacter(filteredGroup);
        middle.push(character);
        if (options.noRepeatedCharacters) {
          used.add(character);
        }
      }
    }

    while (
      fixedPrefix.length + middle.length + fixedSuffix.length <
      options.length
    ) {
      const availablePool = options.noRepeatedCharacters
        ? pools.all
            .split("")
            .filter((character) => !used.has(character))
            .join("")
        : pools.all;

      if (!availablePool) {
        break;
      }

      const nextCharacter = pickRandomCharacter(availablePool);
      middle.push(nextCharacter);
      if (options.noRepeatedCharacters) {
        used.add(nextCharacter);
      }
    }

    if (
      fixedPrefix.length + middle.length + fixedSuffix.length !==
      options.length
    ) {
      continue;
    }

    const candidate = [
      fixedPrefix,
      ...shuffleCharacters(middle),
      fixedSuffix,
    ].join("");

    if (
      options.noRepeatedCharacters &&
      new Set(candidate.split("")).size !== candidate.length
    ) {
      continue;
    }

    if (
      options.avoidSequentialCharacters &&
      hasSequentialCharacters(candidate)
    ) {
      continue;
    }

    if (options.requireEveryType) {
      const requirements = [
        !options.includeLowercase || /[a-z]/.test(candidate),
        !options.includeUppercase || /[A-Z]/.test(candidate),
        !options.includeNumbers || /\d/.test(candidate),
        !options.includeSymbols ||
          candidate
            .split("")
            .some((character) => pools.symbols.includes(character)),
      ];

      if (requirements.some((item) => !item)) {
        continue;
      }
    }

    return candidate;
  }

  return "";
};

const validatePassphraseConfiguration = (options: PassphraseOptions) => {
  if (options.wordCount < 2) {
    return "Use at least two words for the passphrase.";
  }

  if (options.uniqueWords && options.wordCount > WORD_BANK.length) {
    return "Reduce the word count or allow repeated words.";
  }

  if (options.appendSymbol && !options.symbolPool.trim()) {
    return "Add at least one symbol for the passphrase suffix.";
  }

  return "";
};

const generatePassphrase = (options: PassphraseOptions) => {
  const chosenWords: string[] = [];
  const availableWords = [...WORD_BANK];

  for (let index = 0; index < options.wordCount; index += 1) {
    const source = options.uniqueWords ? availableWords : WORD_BANK;
    const selectedIndex = getCryptoRandomInt(source.length);
    const selectedWord = source[selectedIndex];

    chosenWords.push(applyWordStyle(selectedWord, options.wordStyle));

    if (options.uniqueWords) {
      availableWords.splice(selectedIndex, 1);
    }
  }

  let phrase = chosenWords.join(options.separator);

  if (options.appendNumber) {
    const suffix = Array.from({ length: options.numberDigits }, () =>
      pickRandomCharacter(NUMBER_CHARS),
    ).join("");
    phrase += `${options.separator}${suffix}`;
  }

  if (options.appendSymbol) {
    const symbolPool = uniqueCharacters(options.symbolPool) || FALLBACK_SYMBOLS;
    phrase += `${options.separator}${pickRandomCharacter(symbolPool)}`;
  }

  return phrase;
};

const validatePinConfiguration = (options: PinOptions) => {
  if (options.noRepeatedDigits && options.length > 10) {
    return "A PIN without repeated digits cannot be longer than 10 digits.";
  }

  return "";
};

const generatePin = (options: PinOptions) => {
  for (let attempt = 0; attempt < 500; attempt += 1) {
    const digits: string[] = [];
    const used = new Set<string>();

    for (let index = 0; index < options.length; index += 1) {
      const basePool =
        index === 0 && options.avoidLeadingZero ? "123456789" : NUMBER_CHARS;
      const pool = options.noRepeatedDigits
        ? basePool
            .split("")
            .filter((digit) => !used.has(digit))
            .join("")
        : basePool;

      if (!pool) {
        break;
      }

      const nextDigit = pickRandomCharacter(pool);
      digits.push(nextDigit);
      if (options.noRepeatedDigits) {
        used.add(nextDigit);
      }
    }

    const candidate = digits.join("");

    if (candidate.length !== options.length) {
      continue;
    }

    if (options.avoidSequentialDigits && hasSequentialCharacters(candidate)) {
      continue;
    }

    return candidate;
  }

  return "";
};

const PasswordGenerator: React.FC = () => {
  const [mode, setMode] = useState<GeneratorMode>("password");
  const [resultCount, setResultCount] = useState(5);
  const [maskResults, setMaskResults] = useState(false);
  const [refreshSeed, setRefreshSeed] = useState(0);
  const [generatedResults, setGeneratedResults] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);

  const [passwordOptions, setPasswordOptions] = useState<PasswordOptions>({
    length: 18,
    includeLowercase: true,
    includeUppercase: true,
    includeNumbers: true,
    includeSymbols: true,
    includeSpaces: false,
    requireEveryType: true,
    excludeSimilar: false,
    excludeAmbiguous: false,
    excludeCharacters: "",
    symbolSet: "basic",
    customSymbols: "!@#$%&*+=?",
    noRepeatedCharacters: false,
    avoidSequentialCharacters: true,
    startWithLetter: false,
    endWithNumber: false,
  });
  const [passphraseOptions, setPassphraseOptions] = useState<PassphraseOptions>(
    {
      wordCount: 5,
      separator: "-",
      wordStyle: "lower",
      uniqueWords: true,
      appendNumber: true,
      numberDigits: 2,
      appendSymbol: false,
      symbolPool: "!@#$%",
    },
  );
  const [pinOptions, setPinOptions] = useState<PinOptions>({
    length: 6,
    avoidLeadingZero: true,
    noRepeatedDigits: false,
    avoidSequentialDigits: true,
  });

  const seoStructuredData = useMemo(
    () =>
      JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Free Password Generator",
          applicationCategory: "SecurityApplication",
          operatingSystem: "Any",
          url: `${window.location.origin}/password-generator`,
          description:
            "Free online password generator with random passwords, passphrases and PINs. Customize length, symbols, exclusions, uniqueness, separators and batch output.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "Random password generator",
            "Memorable passphrase generator",
            "PIN generator",
            "Custom symbols and excluded characters",
            "Strength estimate and entropy bits",
            "Multiple results with copy support",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: passwordFaqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.shashi-sharma.com.np/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Utilities",
              item: "https://www.shashi-sharma.com.np/password-generator",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Password Generator",
              item: "https://www.shashi-sharma.com.np/password-generator",
            },
          ],
        },
      ]),
    [],
  );

  useEffect(() => {
    updateMetaTags({
      title: "Free Password Generator, Passphrase & PIN Tool | Shashi Sharma",
      description:
        "Free online password generator with secure random passwords, memorable passphrases and numeric PINs. Customize symbols, exclusions, uniqueness, length and copy multiple results instantly.",
      keywords:
        "password generator, free password generator, secure password generator, passphrase generator, pin generator, strong password generator, random password generator, password maker online",
      canonical: `${window.location.origin}/password-generator`,
      ogTitle: "Free Password Generator, Passphrase & PIN Tool",
      ogDescription:
        "Generate strong passwords, passphrases and PINs with advanced controls like custom symbols, excluded characters, unique words and copyable batch output.",
      ogUrl: `${window.location.origin}/password-generator`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary",
      twitterTitle: "Free Password Generator, Passphrase & PIN Tool",
      twitterDescription:
        "Create random passwords, memorable passphrases and PINs with rich controls and live strength estimates.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData: seoStructuredData,
    });
  }, [seoStructuredData]);

  const passwordPoolDetails = useMemo(
    () => getPasswordPools(passwordOptions),
    [passwordOptions],
  );

  const generationError = useMemo(() => {
    if (mode === "password") {
      return validatePasswordConfiguration(passwordOptions);
    }

    if (mode === "passphrase") {
      return validatePassphraseConfiguration(passphraseOptions);
    }

    return validatePinConfiguration(pinOptions);
  }, [mode, passwordOptions, passphraseOptions, pinOptions]);

  const entropyBits = useMemo(() => {
    if (mode === "password") {
      return passwordPoolDetails.all.length > 0
        ? passwordOptions.length * Math.log2(passwordPoolDetails.all.length)
        : 0;
    }

    if (mode === "passphrase") {
      const base = passphraseOptions.wordCount * Math.log2(WORD_BANK.length);
      const numberBonus = passphraseOptions.appendNumber
        ? passphraseOptions.numberDigits * Math.log2(10)
        : 0;
      const symbolBonus = passphraseOptions.appendSymbol
        ? Math.log2(
            Math.max(uniqueCharacters(passphraseOptions.symbolPool).length, 1),
          )
        : 0;
      return base + numberBonus + symbolBonus;
    }

    return getPinEntropy(pinOptions);
  }, [
    mode,
    passwordOptions,
    passwordPoolDetails.all.length,
    passphraseOptions,
    pinOptions,
  ]);

  const strengthMeta = useMemo(
    () => getStrengthMeta(mode, entropyBits),
    [entropyBits, mode],
  );

  const activeOptionChips = useMemo(() => {
    if (mode === "password") {
      return [
        `${passwordOptions.length} chars`,
        passwordOptions.includeLowercase ? "Lowercase" : "",
        passwordOptions.includeUppercase ? "Uppercase" : "",
        passwordOptions.includeNumbers ? "Numbers" : "",
        passwordOptions.includeSymbols ? "Symbols" : "",
        passwordOptions.includeSpaces ? "Spaces" : "",
        passwordOptions.requireEveryType ? "Require every type" : "",
        passwordOptions.excludeSimilar ? "Exclude similar" : "",
        passwordOptions.excludeAmbiguous ? "Exclude ambiguous" : "",
        passwordOptions.noRepeatedCharacters ? "No repeats" : "",
        passwordOptions.avoidSequentialCharacters ? "No sequences" : "",
        passwordOptions.startWithLetter ? "Starts with letter" : "",
        passwordOptions.endWithNumber ? "Ends with number" : "",
      ].filter(Boolean);
    }

    if (mode === "passphrase") {
      return [
        `${passphraseOptions.wordCount} words`,
        `Separator: ${passphraseOptions.separator || "none"}`,
        `Style: ${passphraseOptions.wordStyle}`,
        passphraseOptions.uniqueWords ? "Unique words" : "",
        passphraseOptions.appendNumber
          ? `+ ${passphraseOptions.numberDigits} digits`
          : "",
        passphraseOptions.appendSymbol ? "+ symbol" : "",
      ].filter(Boolean);
    }

    return [
      `${pinOptions.length} digits`,
      pinOptions.avoidLeadingZero ? "No leading zero" : "",
      pinOptions.noRepeatedDigits ? "No repeated digits" : "",
      pinOptions.avoidSequentialDigits ? "No sequences" : "",
    ].filter(Boolean);
  }, [mode, passwordOptions, passphraseOptions, pinOptions]);

  useEffect(() => {
    if (generationError) {
      setGeneratedResults([]);
      return;
    }

    const nextResults: string[] = [];

    for (let index = 0; index < resultCount; index += 1) {
      const generatedValue =
        mode === "password"
          ? generatePassword(passwordOptions)
          : mode === "passphrase"
            ? generatePassphrase(passphraseOptions)
            : generatePin(pinOptions);

      if (!generatedValue) {
        setGeneratedResults([]);
        return;
      }

      nextResults.push(generatedValue);
    }

    setGeneratedResults(nextResults);
  }, [
    generationError,
    mode,
    passwordOptions,
    passphraseOptions,
    pinOptions,
    refreshSeed,
    resultCount,
  ]);

  const primaryResult = generatedResults[0] ?? "";

  const handleCopyText = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(message);
    } catch (_error) {
      setFeedback("Clipboard access is not available in this browser.");
    }
  };

  const handleReset = () => {
    setMode("password");
    setResultCount(5);
    setMaskResults(false);
    setRefreshSeed((current) => current + 1);
    setPasswordOptions({
      length: 18,
      includeLowercase: true,
      includeUppercase: true,
      includeNumbers: true,
      includeSymbols: true,
      includeSpaces: false,
      requireEveryType: true,
      excludeSimilar: false,
      excludeAmbiguous: false,
      excludeCharacters: "",
      symbolSet: "basic",
      customSymbols: "!@#$%&*+=?",
      noRepeatedCharacters: false,
      avoidSequentialCharacters: true,
      startWithLetter: false,
      endWithNumber: false,
    });
    setPassphraseOptions({
      wordCount: 5,
      separator: "-",
      wordStyle: "lower",
      uniqueWords: true,
      appendNumber: true,
      numberDigits: 2,
      appendSymbol: false,
      symbolPool: "!@#$%",
    });
    setPinOptions({
      length: 6,
      avoidLeadingZero: true,
      noRepeatedDigits: false,
      avoidSequentialDigits: true,
    });
    setFeedback("Password generator reset to defaults.");
  };

  const modeItems: Array<{
    value: GeneratorMode;
    label: string;
    icon: React.ReactElement;
  }> = [
    {
      value: "password",
      label: "Password",
      icon: <LockOutlinedIcon fontSize="small" />,
    },
    {
      value: "passphrase",
      label: "Passphrase",
      icon: <AutoAwesomeIcon fontSize="small" />,
    },
    {
      value: "pin",
      label: "PIN",
      icon: <DialpadIcon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, flex: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, mb: 1 }}
            >
              Free Password Generator
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 980 }}
            >
              Generate strong passwords, memorable passphrases and secure PINs
              in one place. Tune every detail including length, symbols,
              excluded characters, repeated characters, separators, unique words
              and batch output with live strength feedback.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5 }}
            >
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label="Password, passphrase & PIN"
              />
              <Chip size="small" variant="outlined" label="Advanced controls" />
              <Chip size="small" variant="outlined" label="Batch generation" />
              <Chip size="small" variant="outlined" label="Entropy estimate" />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(360px, 430px) minmax(0, 1fr)",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                order: { xs: 1, sm: 1 },
                position: { sm: "sticky" },
                top: { sm: 88 },
              }}
            >
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Preview
                      </Typography>
                      <Chip
                        size="small"
                        icon={<SecurityIcon />}
                        label={
                          mode === "password"
                            ? "Secure password"
                            : mode === "passphrase"
                              ? "Memorable passphrase"
                              : "Secure PIN"
                        }
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>

                    <Box
                      sx={{
                        borderRadius: 3,
                        border: "1px dashed",
                        borderColor: generationError
                          ? "warning.main"
                          : "divider",
                        backgroundColor: "background.default",
                        p: 2,
                        minHeight: 220,
                      }}
                    >
                      <Stack spacing={1.5} sx={{ height: "100%" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Primary result
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              mt: 0.75,
                              fontWeight: 800,
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                              fontFamily:
                                '"Roboto Mono", "Fira Code", monospace',
                            }}
                          >
                            {primaryResult
                              ? maskResults
                                ? maskSensitiveValue(primaryResult)
                                : primaryResult
                              : "Waiting for a valid configuration"}
                          </Typography>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            color={strengthMeta.color}
                            label={strengthMeta.label}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${formatDisplayNumber(entropyBits)} bits`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${generatedResults.length} result${generatedResults.length === 1 ? "" : "s"}`}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {activeOptionChips.slice(0, 6).map((item) => (
                            <Chip
                              key={item}
                              size="small"
                              variant="outlined"
                              label={item}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Box>

                    {generationError && (
                      <Alert severity="warning">{generationError}</Alert>
                    )}

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={() =>
                          handleCopyText(
                            primaryResult,
                            "Primary result copied.",
                          )
                        }
                        disabled={!primaryResult}
                      >
                        Copy primary
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() =>
                          handleCopyText(
                            generatedResults.join("\n"),
                            "All generated results copied.",
                          )
                        }
                        disabled={!generatedResults.length}
                      >
                        Copy all
                      </Button>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => setRefreshSeed((current) => current + 1)}
                        disabled={Boolean(generationError)}
                      >
                        Regenerate
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {generatedResults.length > 0 && (
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Generated results
                      </Typography>
                      {generatedResults.map((result, index) => (
                        <Box
                          key={`${result}-${index}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) auto",
                            gap: 1,
                            alignItems: "center",
                            p: 1.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.default",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily:
                                '"Roboto Mono", "Fira Code", monospace',
                              wordBreak: "break-word",
                            }}
                          >
                            {maskResults ? maskSensitiveValue(result) : result}
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<ContentCopyIcon />}
                            onClick={() =>
                              handleCopyText(
                                result,
                                `Result ${index + 1} copied.`,
                              )
                            }
                          >
                            Copy
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {feedback && (
                <Alert severity="success" onClose={() => setFeedback(null)}>
                  {feedback}
                </Alert>
              )}
            </Stack>

            <Card variant="outlined" sx={{ order: { xs: 2, sm: 2 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1.5 }}
                    >
                      Generator type
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {modeItems.map((item) => (
                        <Chip
                          key={item.value}
                          icon={item.icon}
                          label={item.label}
                          clickable
                          color={mode === item.value ? "primary" : "default"}
                          variant={mode === item.value ? "filled" : "outlined"}
                          onClick={() => setMode(item.value)}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Divider />

                  <Stack spacing={2.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Output settings
                    </Typography>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {mode === "password"
                          ? `Length: ${passwordOptions.length} characters`
                          : mode === "passphrase"
                            ? `Words: ${passphraseOptions.wordCount}`
                            : `PIN length: ${pinOptions.length} digits`}
                      </Typography>
                      <Slider
                        min={mode === "pin" ? 4 : mode === "passphrase" ? 3 : 8}
                        max={
                          mode === "pin" ? 12 : mode === "passphrase" ? 10 : 64
                        }
                        step={1}
                        value={
                          mode === "password"
                            ? passwordOptions.length
                            : mode === "passphrase"
                              ? passphraseOptions.wordCount
                              : pinOptions.length
                        }
                        onChange={(_event, value) => {
                          const numericValue = value as number;
                          if (mode === "password") {
                            setPasswordOptions((current) => ({
                              ...current,
                              length: numericValue,
                            }));
                          } else if (mode === "passphrase") {
                            setPassphraseOptions((current) => ({
                              ...current,
                              wordCount: numericValue,
                            }));
                          } else {
                            setPinOptions((current) => ({
                              ...current,
                              length: numericValue,
                            }));
                          }
                        }}
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Results per batch: {resultCount}
                      </Typography>
                      <Slider
                        min={1}
                        max={12}
                        step={1}
                        value={resultCount}
                        onChange={(_event, value) =>
                          setResultCount(value as number)
                        }
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={maskResults}
                          onChange={(event) =>
                            setMaskResults(event.target.checked)
                          }
                        />
                      }
                      label="Mask generated results on screen"
                    />
                  </Stack>

                  <Divider />

                  {mode === "password" && (
                    <Stack spacing={2.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Password rules
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.includeLowercase}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  includeLowercase: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Lowercase"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.includeUppercase}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  includeUppercase: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Uppercase"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.includeNumbers}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  includeNumbers: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Numbers"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.includeSymbols}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  includeSymbols: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Symbols"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.includeSpaces}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  includeSpaces: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Allow spaces"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.requireEveryType}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  requireEveryType: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Require every selected type"
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.excludeSimilar}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  excludeSimilar: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Exclude similar characters"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.excludeAmbiguous}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  excludeAmbiguous: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Exclude ambiguous symbols"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.noRepeatedCharacters}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  noRepeatedCharacters: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="No repeated characters"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                passwordOptions.avoidSequentialCharacters
                              }
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  avoidSequentialCharacters:
                                    event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Avoid sequential characters"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.startWithLetter}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  startWithLetter: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Start with a letter"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passwordOptions.endWithNumber}
                              onChange={(event) =>
                                setPasswordOptions((current) => ({
                                  ...current,
                                  endWithNumber: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="End with a number"
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        <FormControl
                          fullWidth
                          disabled={!passwordOptions.includeSymbols}
                        >
                          <InputLabel id="symbol-set-label">
                            Symbol set
                          </InputLabel>
                          <Select
                            labelId="symbol-set-label"
                            label="Symbol set"
                            value={passwordOptions.symbolSet}
                            onChange={(event) =>
                              setPasswordOptions((current) => ({
                                ...current,
                                symbolSet: event.target.value as SymbolSet,
                              }))
                            }
                          >
                            <MenuItem value="basic">Basic symbols</MenuItem>
                            <MenuItem value="extended">
                              Extended symbols
                            </MenuItem>
                            <MenuItem value="custom">Custom symbols</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          label="Exclude characters"
                          placeholder="O0l1{}[]"
                          value={passwordOptions.excludeCharacters}
                          onChange={(event) =>
                            setPasswordOptions((current) => ({
                              ...current,
                              excludeCharacters: event.target.value,
                            }))
                          }
                        />
                      </Box>

                      {passwordOptions.includeSymbols &&
                        passwordOptions.symbolSet === "custom" && (
                          <TextField
                            fullWidth
                            label="Custom symbol pool"
                            placeholder="!@#$%&*+=?"
                            value={passwordOptions.customSymbols}
                            onChange={(event) =>
                              setPasswordOptions((current) => ({
                                ...current,
                                customSymbols: event.target.value,
                              }))
                            }
                          />
                        )}

                      <Alert severity="info">
                        Available pool size: {passwordPoolDetails.all.length}{" "}
                        unique characters. This directly affects the estimated
                        password strength.
                      </Alert>
                    </Stack>
                  )}

                  {mode === "passphrase" && (
                    <Stack spacing={2.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Passphrase rules
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Separator"
                          value={passphraseOptions.separator}
                          onChange={(event) =>
                            setPassphraseOptions((current) => ({
                              ...current,
                              separator: event.target.value,
                            }))
                          }
                          helperText="Use -, _, ., a space, or any custom separator"
                        />
                        <FormControl fullWidth>
                          <InputLabel id="word-style-label">
                            Word style
                          </InputLabel>
                          <Select
                            labelId="word-style-label"
                            label="Word style"
                            value={passphraseOptions.wordStyle}
                            onChange={(event) =>
                              setPassphraseOptions((current) => ({
                                ...current,
                                wordStyle: event.target.value as WordStyle,
                              }))
                            }
                          >
                            <MenuItem value="lower">lowercase</MenuItem>
                            <MenuItem value="title">Title Case</MenuItem>
                            <MenuItem value="upper">UPPERCASE</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passphraseOptions.uniqueWords}
                              onChange={(event) =>
                                setPassphraseOptions((current) => ({
                                  ...current,
                                  uniqueWords: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Use unique words"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passphraseOptions.appendNumber}
                              onChange={(event) =>
                                setPassphraseOptions((current) => ({
                                  ...current,
                                  appendNumber: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Append random digits"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={passphraseOptions.appendSymbol}
                              onChange={(event) =>
                                setPassphraseOptions((current) => ({
                                  ...current,
                                  appendSymbol: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Append symbol"
                        />
                      </Box>

                      {passphraseOptions.appendNumber && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            Appended digits: {passphraseOptions.numberDigits}
                          </Typography>
                          <Slider
                            min={1}
                            max={6}
                            step={1}
                            value={passphraseOptions.numberDigits}
                            onChange={(_event, value) =>
                              setPassphraseOptions((current) => ({
                                ...current,
                                numberDigits: value as number,
                              }))
                            }
                            valueLabelDisplay="auto"
                          />
                        </Box>
                      )}

                      {passphraseOptions.appendSymbol && (
                        <TextField
                          fullWidth
                          label="Symbol pool"
                          placeholder="!@#$%"
                          value={passphraseOptions.symbolPool}
                          onChange={(event) =>
                            setPassphraseOptions((current) => ({
                              ...current,
                              symbolPool: event.target.value,
                            }))
                          }
                        />
                      )}

                      <Alert severity="info">
                        Passphrases are easier to remember and work especially
                        well with password managers, recovery kits and device
                        unlock phrases.
                      </Alert>
                    </Stack>
                  )}

                  {mode === "pin" && (
                    <Stack spacing={2.5}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        PIN rules
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 1.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={pinOptions.avoidLeadingZero}
                              onChange={(event) =>
                                setPinOptions((current) => ({
                                  ...current,
                                  avoidLeadingZero: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Avoid leading zero"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={pinOptions.noRepeatedDigits}
                              onChange={(event) =>
                                setPinOptions((current) => ({
                                  ...current,
                                  noRepeatedDigits: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="No repeated digits"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={pinOptions.avoidSequentialDigits}
                              onChange={(event) =>
                                setPinOptions((current) => ({
                                  ...current,
                                  avoidSequentialDigits: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Avoid sequential digits"
                        />
                      </Box>

                      <Alert severity="info">
                        Numeric PIN mode is useful for devices, demo accounts,
                        vault access and one-time local testing when you need
                        digits only.
                      </Alert>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box component="section">
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 1 }}
                    >
                      Password generator for accounts, apps, teams and daily
                      security
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This utility is designed as a free online password
                      generator for fast, flexible password creation. It
                      supports random passwords, memorable passphrases and
                      secure PIN generation with options that match modern
                      password policies and real-world usability.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Popular password generator use cases
                      </Typography>
                      <Stack spacing={1}>
                        {passwordUseCases.map((item) => (
                          <Typography
                            key={item}
                            variant="body2"
                            color="text.secondary"
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Why this generator is useful
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          • Supports password, passphrase and PIN generation in
                          a single page.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Offers advanced controls like excluded characters,
                          custom symbols and no-repeat rules.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Generates multiple results at once and lets you copy
                          one or all.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Estimates entropy so visitors can tune length and
                          complexity quickly.
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Frequently asked questions
                    </Typography>
                    <Stack spacing={2}>
                      {passwordFaqItems.map((item) => (
                        <Box key={item.question}>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          >
                            {item.question}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  );
};

export default PasswordGenerator;
